import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { TrackedCompetition } from '@/api/market'
import { useNotLivePage } from '@/app/notLive'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, DataTable, EmptyState, formatCurrency, formatInteger, LoadingBlock, type Column } from '@/design'
import { formatDateKey } from '@/lib/filters'

import { AddTrackedCompetitionForm } from './AddTrackedCompetitionForm'
import { TrackedStatusBadge } from './badges'
import { CsvImportField } from './CsvImportField'
import { useDrillDown, useImportTrackedCompetitionsCsv, useMarketQuery, useMoney } from './hooks'
import { NotLiveSection } from './NotLiveSection'
import './market.css'

function columns(money: ReturnType<typeof useMoney>, href: ReturnType<typeof useDrillDown>): Column<TrackedCompetition>[] {
  return [
    {
      key: 'prize',
      header: 'Prize',
      sortValue: (row) => row.prize,
      cell: (row) => (
        <span className="mk-cell-stack">
          <span>{row.prize}</span>
          <span className="mk-cell-sub">
            <Link to={href(row.drillDown)}>{row.competitorName}</Link>
          </span>
        </span>
      ),
    },
    { key: 'category', header: 'Category', sortValue: (row) => row.category, cell: (row) => row.category },
    { key: 'price', header: 'Ticket price', align: 'end', sortValue: (row) => row.ticketPrice, cell: (row) => formatCurrency(row.ticketPrice, money) },
    { key: 'max', header: 'Max entries', align: 'end', sortValue: (row) => row.maxEntries, cell: (row) => formatInteger(row.maxEntries) },
    { key: 'instant', header: 'Instant wins', align: 'end', sortValue: (row) => row.instantWins, cell: (row) => formatInteger(row.instantWins) },
    { key: 'starts', header: 'Starts', sortValue: (row) => row.startDate, cell: (row) => formatDateKey(row.startDate, true) },
    { key: 'ends', header: 'Ends', sortValue: (row) => row.endDate, cell: (row) => formatDateKey(row.endDate, true) },
    { key: 'status', header: 'Status', sortValue: (row) => row.status, cell: (row) => <TrackedStatusBadge status={row.status} /> },
    { key: 'promo', header: 'Promotional messaging', cell: (row) => row.promotionalMessaging || '—' },
  ]
}

/** Every tracked competition across every competitor, however it entered the system. */
export function TrackedCompetitionsPage({ route }: { route: RouteDef }) {
  const notLive = useNotLivePage('marketTrackedCompetitions')
  const tracked = useMarketQuery('tracked-competitions', (client, filters) => client.marketTrackedCompetitions(filters))
  const competitors = useMarketQuery('competitors', (client, filters) => client.marketCompetitors(filters))
  const href = useDrillDown()
  const money = useMoney()
  const [showAddForm, setShowAddForm] = useState(false)

  if (notLive) return <NotLiveSection route={route} />

  return (
    <SectionFrame
      route={route}
      actions={
        <button type="button" className="ci-button" data-variant="primary" onClick={() => setShowAddForm((current) => !current)}>
          {showAddForm ? 'Close' : 'Add tracked competition'}
        </button>
      }
    >
      <div className="ci-stack">
        {showAddForm ? (
          <Card title="Add or import tracked competitions">
            <AddTrackedCompetitionForm competitors={competitors.data?.data ?? []} onDone={() => setShowAddForm(false)} />
            <CsvImportBlock />
          </Card>
        ) : null}

        <Card title="Tracked competitions" subtitle="Prize, price, entries and status across every tracked competitor" busy={tracked.isFetching}>
          {tracked.isPending ? <LoadingBlock rows={6} /> : null}
          {tracked.isError ? <EmptyState variant="outage" compact description="Tracked competitions could not be loaded." /> : null}
          {tracked.data ? (
            <DataTable
              caption="Tracked competitions"
              columns={columns(money, href)}
              rows={tracked.data.data}
              rowKey={(row) => row.id}
              initialSort={{ key: 'ends', direction: 'asc' }}
              empty={
                <EmptyState
                  variant="no-data"
                  compact
                  title="No tracked competitions yet"
                  description="Add one manually or import a CSV above, once you have at least one competitor."
                />
              }
            />
          ) : null}
        </Card>
      </div>
    </SectionFrame>
  )
}

function CsvImportBlock() {
  const importCsv = useImportTrackedCompetitionsCsv()
  return (
    <CsvImportField
      label="Or import tracked competitions via CSV"
      header="competitorName,prize,category,ticketPrice,maxEntries,instantWins,startDate,endDate,promotionalMessaging"
      onImport={(csv) => importCsv.mutateAsync(csv)}
    />
  )
}
